import * as React from 'react';
import { Link } from 'react-router';
import styled, { withTheme } from 'styled-components';
import { rem } from 'polished';
import { TextP } from '../../Defaults/Text';
import {
  FlexCol,
  FlexRowCenter,
  FlexRowCenterVertical,
} from '../../Defaults/Flexbox/Flexbox';
import { Button, ButtonLink } from './Button';
import { __ } from 'react-i18n';
import { SvgIcon, IconType } from './SvgIcon';

enum InfoType {
  ERROR = 'ERROR',
  SUCCESS = 'SUCCESS',
  INFO = 'INFO',
}

export interface Props {
  info: any;
  type?: InfoType;
  theme: any;
  noIcon?: boolean;
  marginBottom?: number;
  maxWidth?: number;
}

function InfoText({
  info,
  type,
  theme,
  noIcon,
  maxWidth,
  marginBottom = 56,
}: Props) {
  const { ERROR, SUCCESS, INFO } = InfoType;

  const loadBackgroundColorBasedOnType = () => {
    switch (type) {
      case ERROR: {
        return theme.infoTexts.bgError;
      }
      case SUCCESS: {
        return theme.infoTexts.bgSucces;
      }
      case INFO: {
        return theme.infoTexts.bgInfo;
      }
      default: {
        return theme.infoTexts.bgInfo;
      }
    }
  };

  const loadColorBasedOnType = () => {
    switch (type) {
      case ERROR: {
        return theme.infoTexts.colorError;
      }
      case SUCCESS: {
        return theme.infoTexts.colorSuccess;
      }
      case INFO: {
        return theme.infoTexts.colorInfo;
      }
      default: {
        return theme.infoTexts.colorInfo;
      }
    }
  };

  const loadBorderBasedOnType = () => {
    switch (type) {
      case ERROR: {
        return 'none';
      }
      case SUCCESS: {
        return 'none';
      }
      case INFO: {
        return `${rem(1)} solid ${theme.infoTexts.borderInfo}`;
      }
      default: {
        return 'none';
      }
    }
  };

  const loadIconBasedOnType = () => {
    switch (type) {
      case ERROR: {
        return IconType.error;
      }
      case SUCCESS: {
        return IconType.success;
      }
      case INFO: {
        return IconType.success;
      }
      default: {
        return IconType.success;
      }
    }
  };

  const bgColor = loadBackgroundColorBasedOnType();
  const color = loadColorBasedOnType();
  const border = loadBorderBasedOnType();
  const icon = loadIconBasedOnType();

  return (
    <Wrapper
      maxWidth={maxWidth}
      marginBottom={marginBottom}
      bg={bgColor}
      border={border}
    >
      {!noIcon && (
        <SvgIcon
          fill={color}
          width={24}
          height={24}
          icon={icon}
          alt="info"
          title="info"
          marginRight={16}
        />
      )}
      <Text color={color}>{info}</Text>
    </Wrapper>
  );
}

interface WrapperProps {
  bg: string;
  border: string;
  marginBottom: number;
  maxWidth?: number;
}

const Wrapper = styled(FlexRowCenterVertical)`
  text-decoration: none;
  margin: 0 auto;
  margin-bottom: ${({ marginBottom }) => rem(marginBottom)};
  width: 100%;
  max-width: ${({ maxWidth }) => (maxWidth ? rem(maxWidth) : 'initial')};
  border-radius: ${rem(2)};
  border: ${({ border }) => border};
  padding: ${rem(12)} ${rem(16)};
  background: ${(props: WrapperProps) => props.bg};
  align-items: flex-start;
`;

const Text = styled(TextP)<{ color: string }>`
  font-size: ${rem(14)};
  color: ${({ color }) => color};
  line-height: ${rem(22)};
  font-weight: 400;
  width: 100%;

  ${({ theme }) => theme.mediab.l925`
     font-size: ${rem(13)};
  `}
`;

const withThemeInfoText: any = withTheme(InfoText);
export { withThemeInfoText as InfoText, InfoType };
